import { useCallback, useEffect, useState } from 'react'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { imageSize } from '../data/images'

export type HeroSlide = {
  image: string
  alt: string
  label: string
  position?: string
}

const ROTATION_MS = 6200

export default function HeroSlideshow({ slides }: { slides: HeroSlide[] }) {
  const [activeIndex, setActiveIndex] = useState(0)
  const [readySlides, setReadySlides] = useState(() => new Set([0]))
  const [pageVisible, setPageVisible] = useState(() => !document.hidden)
  const [reduceMotion, setReduceMotion] = useState(
    () => window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  )

  const selectSlide = useCallback((index: number) => {
    setActiveIndex((index + slides.length) % slides.length)
  }, [slides.length])

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const handlePreference = () => setReduceMotion(mediaQuery.matches)
    const handleVisibility = () => setPageVisible(!document.hidden)

    mediaQuery.addEventListener('change', handlePreference)
    document.addEventListener('visibilitychange', handleVisibility)
    return () => {
      mediaQuery.removeEventListener('change', handlePreference)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    let timeoutId = 0

    const preloadRemainingSlides = () => {
      slides.slice(1).forEach((slide, offset) => {
        const image = new Image()
        const index = offset + 1
        image.onload = () => {
          if (!cancelled) {
            setReadySlides((current) => new Set([...current, index]))
          }
        }
        image.src = `${import.meta.env.BASE_URL}images/${slide.image}`
      })
    }

    const schedulePreload = () => {
      timeoutId = window.setTimeout(preloadRemainingSlides, 700)
    }

    if (document.readyState === 'complete') schedulePreload()
    else window.addEventListener('load', schedulePreload, { once: true })

    return () => {
      cancelled = true
      window.clearTimeout(timeoutId)
      window.removeEventListener('load', schedulePreload)
    }
  }, [slides])

  useEffect(() => {
    if (reduceMotion || !pageVisible || readySlides.size < slides.length) return
    const intervalId = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % slides.length)
    }, ROTATION_MS)
    return () => window.clearInterval(intervalId)
  }, [pageVisible, readySlides.size, reduceMotion, slides.length])

  return (
    <div
      className={`hero-slideshow${reduceMotion || !pageVisible ? ' is-paused' : ''}`}
      aria-roledescription="carousel"
      aria-label="Diamond Suites Ocala property"
    >
      <div className="hero-slideshow__stage">
        {slides.map((slide, index) => readySlides.has(index) && (
          <figure
            className={`hero-slideshow__slide${activeIndex === index ? ' is-active' : ''}`}
            key={slide.image}
            aria-hidden={activeIndex !== index}
          >
            <img
              src={`${import.meta.env.BASE_URL}images/${slide.image}`}
              alt={activeIndex === index ? slide.alt : ''}
              {...imageSize(slide.image)}
              style={{ objectPosition: slide.position }}
              fetchPriority={index === 0 ? 'high' : 'auto'}
              loading={index === 0 ? 'eager' : 'lazy'}
            />
          </figure>
        ))}
        <div className="hero-slideshow__wash" aria-hidden="true" />
        <div className="hero-slideshow__caption">
          <p key={activeIndex}>
            <span>{String(activeIndex + 1).padStart(2, '0')} / {String(slides.length).padStart(2, '0')}</span>
            {slides[activeIndex].label}
          </p>
          <small>Diamond Suites Ocala</small>
        </div>
      </div>

      <div className="hero-slideshow__controls">
        <button
          type="button"
          onClick={() => selectSlide(activeIndex - 1)}
          aria-label="Show previous property image"
          disabled={readySlides.size < slides.length}
        >
          <ArrowLeft aria-hidden="true" />
        </button>
        <div className="hero-slideshow__progress-wrap" aria-label={`Slide ${activeIndex + 1} of ${slides.length}`}>
          <span>{String(activeIndex + 1).padStart(2, '0')}</span>
          <div className="hero-slideshow__progress" aria-hidden="true">
            <i key={activeIndex} />
          </div>
          <span>{String(slides.length).padStart(2, '0')}</span>
        </div>
        <button
          type="button"
          onClick={() => selectSlide(activeIndex + 1)}
          aria-label="Show next property image"
          disabled={readySlides.size < slides.length}
        >
          <ArrowRight aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}
